/*  ==|== Responsive =============================================================
 Author: James Jackson-South
 twitter : http://twitter.com/James_M_South
 github : https://github.com/ResponsiveBP/Responsive
 Copyright (c),  James Jackson-South.
 Licensed under the MIT License.
 ============================================================================== */

/*! Responsive v4.1.4 | MIT License | responsivebp.com */

import $ from "jquery";
import { createResponsiveEventsName, keys, onTransitionEnd, pseudoUnique, redraw, supportRtl } from "./responsive.core";

declare global {
  interface Window {
    RESPONSIVE_TABS?: boolean;
  }
}

const ns = ".r.tabs";
const da = ".data-api";

const { einit, eshown, eshow, echanged } = createResponsiveEventsName(ns, da);

class Tabs {
  private $element: JQuery;
  private tabbing: boolean | null = null;

  constructor(element: HTMLElement) {
    this.$element = $(element);
  }

  init() {
    this.initAccessibility();
    this.bindEvents();
  }

  private initAccessibility(): void {
    const $tablist = this.$element.children("ul:first").attr("role", "tablist");
    const $triggers = $tablist.children().attr("role", "presentation");
    const $panes = this.$element.children(":not(ul)");
    const id = pseudoUnique();
    const activeIndex = $tablist.find("[aria-selected=true]").parent().index();
    const hasActive = activeIndex > -1;

    $triggers.each(function(index)
    {
      const $this = $(this);
      const $tab = $this.children("a");
      const isActive = (
        hasActive && index === activeIndex
      ) || (
        !hasActive && index === 0
      );

      $tab.attr({
        role: "tab",
        id: `tab-${id}-${index}`,
        "aria-controls": `pane-${id}-${index}`,
        "aria-selected": isActive,
        tabindex: 0,
      });

      $panes.eq(index).attr({
        role: "tabpanel",
        id: `pane-${id}-${index}`,
        "aria-labelledby": `tab-${id}-${index}`,
        tabindex: isActive ? 0 : -1,
      });
    });
  }

  private bindEvents(): void {
    this.$element.on("click", "ul[role=tablist] > li > [role=tab]", (e) => this.click(e));
    this.$element.on("keydown", "ul[role=tablist] > li > [role=tab]", (e) => this.keydown(e));
  }

  public show(position: number): boolean | void {
    const $activeItem = this.$element.children("ul").find("[aria-selected=true]");
    const $children = $activeItem.closest("ul").children();
    const activePosition = $activeItem.parent().index();

    if (position > (
      $children.length - 1
    ) || position < 0) {
      return;
    }

    if (activePosition === position) {
      return;
    }

    this.tab(activePosition, position, ($item: JQuery) =>
    {
      const complete = () =>
        {
          this.tabbing = false;
          $item.siblings().addBack().removeClass("fade-out fade-in");
          this.$element.trigger($.Event(eshown, { relatedTarget: $item[0] }));
        };

      onTransitionEnd($item.get(0), complete);
    });
  }

  private tab(activePosition: number, position: number, callback: (item: JQuery) => void): void {
    const showEvent = $.Event(eshow);
    const $childTabs = this.$element.children("ul").children("li");
    const $childPanes = this.$element.children(":not(ul)");
    const $nextTab = $childTabs.eq(position);
    const $currentPane = $childPanes.eq(activePosition);
    const $nextPane = $childPanes.eq(position);

    this.$element.trigger(showEvent);

    if (this.tabbing || showEvent.isDefaultPrevented()) return;

    this.tabbing = true;

    $childTabs.children("a").attr("aria-selected", "false");
    $nextTab.children("a").attr("aria-selected", "true").focus();

    $currentPane.addClass("fade-out fade-in");
    $nextPane.attr("tabIndex", 0).addClass("fade-out");
    $childPanes.filter(".fade-in").attr("tabIndex", -1).removeClass("fade-in");

    redraw($nextPane);
    $nextPane.addClass("fade-in");

    callback($nextPane);
  }

  private click(event: JQuery.ClickEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const $li = $(event.target as HTMLElement).parent();
    const index = $li.index();
    this.show(index);
  }

  private keydown(event: JQuery.KeyDownEvent): void {
    const which = (
      event as JQuery.KeyDownEvent
    ).which;
    if (![keys.SPACE, keys.LEFT, keys.RIGHT].includes(which)) return;

    event.preventDefault();
    event.stopPropagation();

    const $li = $(event.target as HTMLElement).parent();
    const $all = $li.siblings().addBack();
    const length = $all.length;
    let index = $li.index();

    if (which === keys.SPACE) {
      this.show(index);
      return;
    }

    index = which === keys.LEFT ? (
      supportRtl() ? index + 1 : index - 1
    ) : (
      supportRtl() ? index - 1 : index + 1
    );
    index = (
      index + length
    ) % length;

    this.show(index);
  }
}


const init = (): void =>
  {
    if (window.RESPONSIVE_TABS) return;
    window.RESPONSIVE_TABS = true;

    const prepare = () =>
      {
        $("[data-tabs]").each(function()
        {
          const $this = $(this);
          if (!$this.data("r.tabsLoaded")) {
            $this.data("r.tabsLoaded", true);
            const tabs = new Tabs(this);
            tabs.init();
            $this.data("r.tabs", tabs);
          }
        });
      };

    $(document)
      .on([einit, echanged].join(" "), prepare)
      .ready(() => $(document).trigger(einit));
  };

export default { init };
